import { BaseSelectorOption } from "@/types";
import { Autocomplete, AutocompleteRenderOptionState, SxProps } from "@mui/material";
import { Component, HTMLAttributes, ReactNode } from "react";
import { AppContext } from "../../common/AppContext";
import DropIcon from "../../resources/images/icons-chevron-double.svg";
import RemoveIcon from "../../resources/images/icons-remove.svg";
import { TextField } from "../TextField";

interface MultipleFilterProps<TOption extends BaseSelectorOption = BaseSelectorOption> {
  label?: string;
  options: TOption[];
  value?: TOption[];
  getItemLabel?: (option: TOption) => string;
  onChange?: (event: any, selected: TOption[]) => void;
  sx?: SxProps;
  placeholder?: string;
  focusPlaceholder?: string;
  multiple?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  loading?: boolean;
  noOptionsText?: string;
  translate?: (key: string, options?: Record<string, unknown>) => string;
  filterOptions?: (options: TOption[], state: any) => TOption[];
  renderOption?: (
    props: HTMLAttributes<HTMLLIElement> & { key: any },
    option: TOption,
    state?: AutocompleteRenderOptionState
  ) => ReactNode;
  allowMultiple?: boolean;
  isOptionEqualToValue?: (option: TOption, value: TOption) => boolean;
}

export class MultipleFilter<
  TOption extends BaseSelectorOption = BaseSelectorOption
> extends Component<MultipleFilterProps<TOption>> {
  static contextType = AppContext;

  static defaultProps = {
    listType: "Filter",
    maxResults: 50,
    initialSearch: false,
    multiple: true,
    clearable: true,
    hasNoneItem: false,
    hasAllItem: false,
    getItemId: null,
    debounce: true,
    clearOnSelection: false,
    clearResultsOnSelection: false,
    excludeItems: [],
    matchFrom: "any" //start or any
  };

  initialItems: any;
  value: any;
  debounceLoadItems: any;
  dataList: any;

  constructor(props: MultipleFilterProps<TOption>) {
    super(props);
  }

  onChange(e: any, selected: any) {
    const { onChange } = this.props;

    onChange?.(e, selected);
  }

  isOptionEqualToValue(option: TOption, value: TOption): boolean {
    // Compare by key first (used by filter options), then by id, then by label
    if ("key" in option && "key" in value) {
      return (option as any).key === (value as any).key;
    }
    if (option.id !== undefined && value.id !== undefined) {
      return option.id === value.id;
    }
    const optionLabel = this.props.getItemLabel?.(option) ?? option.label ?? option.name;
    const valueLabel = this.props.getItemLabel?.(value) ?? value.label ?? value.name;
    return optionLabel === valueLabel;
  }

  render() {
    const {
      label,
      sx,
      placeholder,
      focusPlaceholder,
      multiple,
      clearable,
      disabled,
      loading,
      noOptionsText,
      translate,
      value,
      options,
      getItemLabel,
      filterOptions,
      renderOption,
      isOptionEqualToValue
    } = this.props;

    // Use translated noOptionsText if translate function is available
    const effectiveNoOptionsText =
      noOptionsText || translate?.("common.noSearchResults") || "No search results";

    return (
      <Autocomplete
        className='klayo-filterselector klayo-selector'
        options={options}
        value={value}
        getOptionLabel={getItemLabel}
        filterOptions={filterOptions}
        isOptionEqualToValue={isOptionEqualToValue ?? this.isOptionEqualToValue.bind(this)}
        autoComplete
        disableClearable={!clearable}
        filterSelectedOptions
        popupIcon={<DropIcon />}
        clearIcon={<RemoveIcon />}
        noOptionsText={effectiveNoOptionsText}
        multiple={multiple}
        renderOption={renderOption}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            disabled={disabled}
            loading={loading}
            placeholder={placeholder}
            focusPlaceholder={focusPlaceholder}
            sx={{ width: "100%" }}
            endAdornment={params.InputProps.endAdornment}
          />
        )}
        onChange={this.onChange.bind(this)}
        sx={sx}
      />
    );
  }
}
